import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { howtoApi } from '../../api/client';
import type { HowToCategory, HowToGuide } from '../../api/modules/howto';
import { useAuth } from '../../hooks/useAuth';

export function useHelpGuideDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [guide, setGuide] = useState<HowToGuide | null>(null);
  const [categories, setCategories] = useState<HowToCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isAuthenticated || !slug) return;

    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const [{ guide: data }, categoriesRes] = await Promise.all([
          howtoApi.getGuideBySlug(slug),
          howtoApi.listCategories(),
        ]);
        setGuide(data);
        setCategories(categoriesRes.categories);
      } catch (err: any) {
        console.error('Failed to load help guide:', err);
        setError(err?.message || 'Guide not found');
        setGuide(null);
        try {
          const categoriesRes = await howtoApi.listCategories();
          setCategories(categoriesRes.categories);
        } catch {
          // ignore secondary failure
        }
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [authLoading, isAuthenticated, slug, navigate]);

  const selectCategory = (categoryId: string) => {
    if (categoryId === 'all') {
      navigate('/help');
      return;
    }
    navigate(`/help?category=${encodeURIComponent(categoryId)}`);
  };

  return {
    authLoading,
    isAuthenticated,
    isLoading,
    guide,
    categories,
    error,
    selectCategory,
  };
}
